import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ButtonComponent } from '@vg-constellation/angular-17/button';
import { IconComponent, IconModule } from '@vg-constellation/angular-17/icon';
import { LinkComponent } from '@vg-constellation/angular-17/link';
import { ModalDialogComponent } from '@vg-constellation/angular-17/modal-dialog';
import { HintErrorComponent } from '@vg-constellation/angular-17/hint-error';
import { DropdownMenuItem } from '@vg-constellation/angular-17/dropdown';

import {
  BeneficiarySelection,
  BeneficiarySectionType,
  BeneficiaryState,
  calculateDistributionTotal,
  createEmptyBeneficiaryState,
} from '@models/beneficiary.model';
import { BeneficiaryCardComponent } from '../beneficiary-card/beneficiary-card.component';
import { InputBeneficiaryCardComponent } from '../input-beneficiary-card/input-beneficiary-card.component';
import { AccountType } from '../../types/AccountType';
import { ZokService, PersonCreate, PersonUpdate, Person } from '@services/zok';
import { BeneficiaryDuplicateDetectionService } from '@services/beneficiary-duplicate-detection/beneficiary-duplicate-detection.service';
import { WebLogger } from '@weps/shared-http';
import * as content from '@services/content/guided-flow/account-details-content';

@Component({
  selector: 'app-beneficiary-section',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    IconModule,
    LinkComponent,
    ModalDialogComponent,
    HintErrorComponent,
    BeneficiaryCardComponent,
    InputBeneficiaryCardComponent,
  ],
  templateUrl: './beneficiary-section.component.html',
  styleUrls: ['./beneficiary-section.component.scss'],
})
export class BeneficiarySectionComponent implements OnInit {
  private readonly zokService = inject(ZokService);
  private readonly dupDetection = inject(BeneficiaryDuplicateDetectionService);

  private logEvent(eventType: string, payload: Record<string, unknown> = {}, level: 'info' | 'warn' | 'error' = 'info'): void {
    WebLogger.log({ message: `Beneficiary section: ${eventType}`, level, payload: { event_type: eventType, ...payload } });
  }

  @Input() accountType: AccountType;
  @Input() initialBeneficiaryState: BeneficiaryState | null = null;

  @Output() beneficiaryStateChange = new EventEmitter<BeneficiaryState>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() helpClick = new EventEmitter<void>();
  @Output() savingChange = new EventEmitter<boolean>();

  @ViewChild('removeModal') removeModal: ModalDialogComponent;
  @ViewChild('duplicateModal') duplicateModal: ModalDialogComponent;

  protected readonly content = content;

  // Beneficiary data
  beneficiaryState: BeneficiaryState = createEmptyBeneficiaryState();

  // UI state — only one section can be adding at a time
  addingSection: BeneficiarySectionType | null = null;
  isSaving = false;
  editingBeneficiary: BeneficiarySelection | null = null;
  editingSection: BeneficiarySectionType | null = null;
  beneficiaryToRemove: BeneficiarySelection | null = null;
  removeSection: BeneficiarySectionType | null = null;
  protected inputCardResetKey = 0; // Used to force component recreation

  // Duplicate detection state
  pendingBeneficiary: BeneficiarySelection | null = null;
  pendingSaveSection: BeneficiarySectionType | null = null;
  duplicateMatchName = '';

  // Validation state
  showRequiredError = false;
  showIncompleteError = false;
  showPrimaryDistributionError = false;
  showBackupDistributionError = false;
  showDuplicatePersonError = false;
  showCreatePersonError = false;
  showHouseholdLoadError = false;

  // Household members loaded from ZOK
  householdMembers: DropdownMenuItem[] = [];
  householdPeople: Person[] = [];

  ngOnInit(): void {
    // Restore persisted beneficiary state from guided flow (Case A: intra-session navigation)
    if (this.initialBeneficiaryState && this.hasPersistedBeneficiaries(this.initialBeneficiaryState)) {
      this.beneficiaryState = { ...this.initialBeneficiaryState };
      this.dupDetection.rebuildFromState(this.beneficiaryState);
    }

    this.loadHouseholdMembers();

    // Only auto-open the input card when there are no beneficiaries to display
    if (!this.hasBeneficiaries) {
      this.addingSection = 'PRIMARY';
    }
  }

  private hasPersistedBeneficiaries(state: BeneficiaryState): boolean {
    return (state.primaryBeneficiaries?.length > 0 || state.backupBeneficiaries?.length > 0);
  }

  private loadHouseholdMembers(): void {
    this.zokService.syncPeople({}).subscribe({
      next: (response) => {
        this.householdPeople = response.householdMembers;
        this.householdMembers = response.householdMembers.map(person => ({
          labelText: `${person.name.firstName} ${person.name.lastName}`,
          id: person.householdMemberId,
        }));
        this.showHouseholdLoadError = false;
        this.logEvent('beneficiary_household_loaded', {
          people_count: response.householdMembers.length,
          member_ids: response.householdMembers.map(p => p.householdMemberId),
        });
      },
      error: (err) => {
        this.householdMembers = [];
        this.showHouseholdLoadError = true;
        this.logEvent('beneficiary_household_load_failed', {
          status_code: err?.status,
          error_message: err?.message,
          error_detail: err?.error?.error?.message || err?.error?.message,
        }, 'error');
      },
    });
  }

  get primaryTotal(): number {
    return calculateDistributionTotal(this.beneficiaryState.primaryBeneficiaries);
  }

  get backupTotal(): number {
    return calculateDistributionTotal(this.beneficiaryState.backupBeneficiaries);
  }

  get hasBeneficiaries(): boolean {
    return this.beneficiaryState.primaryBeneficiaries.length > 0 ||
           this.beneficiaryState.backupBeneficiaries.length > 0;
  }

  get isPrimaryValid(): boolean {
    return this.beneficiaryState.primaryBeneficiaries.length === 0 || this.primaryTotal === 100;
  }

  get isBackupValid(): boolean {
    return this.beneficiaryState.backupBeneficiaries.length === 0 || this.backupTotal === 100;
  }

  get isInputCardOpen(): boolean {
    return this.addingSection !== null || this.editingBeneficiary !== null;
  }

  trackByBeneficiary(index: number, bene: BeneficiarySelection): string {
    return bene.householdMemberId || `${bene.firstName}-${bene.lastName}-${index}`;
  }

  handleHelpClick(): void {
    this.helpClick.emit();
  }

  // --- Add beneficiary ---

  handleAdd(section: BeneficiarySectionType): void {
    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return;
    }
    if (section === 'CONTINGENT' && this.beneficiaryState.primaryBeneficiaries.length === 0) {
      this.logEvent('backup_blocked_no_primary', {}, 'warn');
      return;
    }
    this.showIncompleteError = false;
    this.addingSection = section;
  }

  // --- Save beneficiary (from InputBeneficiaryCard) ---

  handleSave(bene: BeneficiarySelection, section: BeneficiarySectionType): void {
    bene.type = section;

    // DOB-based duplicate check: only for genuinely new persons (not existing household members),
    // and only when DOB was provided. Both ZOK and c11n-date-picker use YYYY-MM-DD format.
    if (!this.editingBeneficiary && bene.isNew && bene.dateOfBirth) {
      const duplicate = this.dupDetection.findByDob(bene);
      if (duplicate) {
        this.pendingBeneficiary = bene;
        this.pendingSaveSection = section;
        this.duplicateMatchName = `${duplicate.firstName} ${duplicate.lastName}`;
        this.duplicateModal?.openModalDialog();
        return;
      }
    }

    // If new person, create in ZOK first
    if (bene.isNew && !bene.householdMemberId) {
      this.createPersonInZok(bene, (savedBene) => this.commitSave(savedBene, section));
    } else {
      this.commitSave(bene, section);
    }
  }

  private commitSave(bene: BeneficiarySelection, section: BeneficiarySectionType): void {
    const list = this.getBeneficiariesForSection(section);

    if (this.editingBeneficiary && this.editingSection === section) {
      // Edit mode: if the person exists in ZOK, update them via PUT before saving locally
      if (bene.householdMemberId && !bene.isNew) {
        this.updatePersonInZok(bene, () => {
          const editIndex = list.indexOf(this.editingBeneficiary!);
          if (editIndex >= 0) {
            this.dupDetection.remove(list[editIndex]);
            list[editIndex] = bene;
            this.dupDetection.add(bene);
          }
          this.closeInputCard();
          this.emitState();
        });
        return;
      }
      const editIndex = list.indexOf(this.editingBeneficiary);
      if (editIndex >= 0) {
        this.dupDetection.remove(list[editIndex]);
        list[editIndex] = bene;
        this.dupDetection.add(bene);
      }
    } else {
      if (this.dupDetection.isDuplicateInSection(bene, list)) {
        this.showDuplicatePersonError = true;
        return;
      }
      list.push(bene);
      this.dupDetection.add(bene);
    }

    this.closeInputCard();
    this.emitState();
  }

  // --- ZOK Integration ---

  private createPersonInZok(bene: BeneficiarySelection, onSuccess: (bene: BeneficiarySelection) => void): void {
    const personCreate: PersonCreate = {
      name: {
        firstName: bene.firstName,
        lastName: bene.lastName,
      },
      birthDate: bene.dateOfBirth,
      state: bene.stateOfResidence,
    };

    this.setSaving(true);
    this.logEvent('beneficiary_create_person_start', {
      has_state: !!personCreate.state,
    });

    this.zokService.createPerson(personCreate).subscribe({
      next: (response) => {
        bene.householdMemberId = response.person.householdMemberId;
        bene.isNew = false;
        this.logEvent('beneficiary_create_person_success', {
          household_member_id: response.person.householdMemberId,
          last_updated_by: response.person.lastUpdatedBy,
        });
        this.loadHouseholdMembers();
        this.setSaving(false);
        onSuccess(bene);
      },
      error: (err) => {
        this.setSaving(false);
        this.showCreatePersonError = true;

        const zokError = err?.error?.error || err?.error;
        this.logEvent('beneficiary_create_person_failed', {
          status_code: err?.status,
          error_message: err?.message,
          zok_error_type: zokError?.type,
          zok_error_message: zokError?.message,
          zok_error_path: zokError?.path,
          zok_error_invalid_value: zokError?.invalidValue,
        }, 'error');
      },
    });
  }

  /**
   * Updates an existing person in ZOK via PUT with the full payload.
   * Starts from the original ZOK record and overwrites only the bene-form fields.
   */
  private updatePersonInZok(bene: BeneficiarySelection, onSuccess: () => void): void {
    const householdMemberId = bene.householdMemberId!;
    const original = this.householdPeople.find(p => p.householdMemberId === householdMemberId);

    // Spread the original record to preserve all existing data (email, referenceIds, relationships, etc.),
    // then overwrite only the fields the bene form edits.
    const { isPrimary: _ip, lastUpdatedBy: _lub, ...baseFields } = original || {} as Person;
    const personUpdate: PersonUpdate = {
      ...baseFields,
      householdMemberId,
      name: { ...original?.name, firstName: bene.firstName, lastName: bene.lastName },
      birthDate: bene.dateOfBirth,
      state: bene.stateOfResidence,
    };

    this.setSaving(true);
    this.logEvent('beneficiary_update_person_start', {
      household_member_id: householdMemberId,
    });

    this.zokService.updatePerson(householdMemberId, personUpdate).subscribe({
      next: (response) => {
        this.logEvent('beneficiary_update_person_success', {
          household_member_id: response.person.householdMemberId,
        });
        this.loadHouseholdMembers();
        this.setSaving(false);
        onSuccess();
      },
      error: (err) => {
        this.setSaving(false);
        this.showCreatePersonError = true;

        const zokError = err?.error?.error || err?.error;
        this.logEvent('beneficiary_update_person_failed', {
          household_member_id: householdMemberId,
          status_code: err?.status,
          error_message: err?.message,
          zok_error_type: zokError?.type,
          zok_error_message: zokError?.message,
        }, 'error');
      },
    });
  }

  private setSaving(value: boolean): void {
    this.isSaving = value;
    this.savingChange.emit(value);
  }

  // --- Cancel ---

  handleCancelInput(): void {
    this.closeInputCard();

    // Reset to initial state if no beneficiaries exist (same as delete logic)
    if (!this.hasBeneficiaries) {
      this.inputCardResetKey++; // Force complete component reset
      this.addingSection = 'PRIMARY';
    }
  }

  private closeInputCard(): void {
    this.addingSection = null;
    this.editingBeneficiary = null;
    this.editingSection = null;
    this.showIncompleteError = false;
    this.showDuplicatePersonError = false;
    this.showCreatePersonError = false;
  }

  // --- Edit ---

  handleEdit(bene: BeneficiarySelection, section: BeneficiarySectionType): void {
    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return;
    }
    this.logEvent('beneficiary_edit_start', {
      household_member_id: bene.householdMemberId,
      section,
    });
    this.editingBeneficiary = bene;
    this.editingSection = section;
  }

  // --- Remove ---

  handleRemove(bene: BeneficiarySelection, section: BeneficiarySectionType): void {
    this.logEvent('beneficiary_remove_modal_open', {
      household_member_id: bene.householdMemberId,
      section,
    });
    this.beneficiaryToRemove = bene;
    this.removeSection = section;
    this.removeModal?.openModalDialog();
  }

  confirmRemove(): void {
    if (!this.beneficiaryToRemove || !this.removeSection) return;

    this.logEvent('beneficiary_removed', {
      household_member_id: this.beneficiaryToRemove.householdMemberId,
      section: this.removeSection,
      remaining_primary: this.beneficiaryState.primaryBeneficiaries.length - (this.removeSection === 'PRIMARY' ? 1 : 0),
      remaining_backup: this.beneficiaryState.backupBeneficiaries.length - (this.removeSection === 'CONTINGENT' ? 1 : 0),
    });

    this.dupDetection.remove(this.beneficiaryToRemove);

    const list = this.getBeneficiariesForSection(this.removeSection);
    this.setBeneficiariesForSection(
      this.removeSection,
      list.filter(b => b !== this.beneficiaryToRemove)
    );

    this.beneficiaryToRemove = null;
    this.removeSection = null;

    // Reset to initial state if all beneficiaries have been removed
    if (!this.hasBeneficiaries) {
      this.addingSection = 'PRIMARY';
    }

    this.emitState();
  }

  cancelRemove(): void {
    this.beneficiaryToRemove = null;
    this.removeSection = null;
  }

  // --- Duplicate detection ---

  confirmAddDuplicate(): void {
    if (!this.pendingBeneficiary || !this.pendingSaveSection) return;

    const bene = this.pendingBeneficiary;
    const section = this.pendingSaveSection;
    this.clearDuplicateState();

    // If new person, create in ZOK first even after duplicate confirmation
    if (bene.isNew && !bene.householdMemberId) {
      this.createPersonInZok(bene, (savedBene) => this.commitSave(savedBene, section));
    } else {
      this.commitSave(bene, section);
    }
  }

  discardDuplicate(): void {
    this.closeInputCard();
    this.clearDuplicateState();
  }

  private clearDuplicateState(): void {
    this.pendingBeneficiary = null;
    this.pendingSaveSection = null;
    this.duplicateMatchName = '';
  }

  // --- Person selection ---

  handlePersonChange(
    event: { beneficiary: BeneficiarySelection; memberId: string },
    section: BeneficiarySectionType
  ): void {
    const list = this.getBeneficiariesForSection(section);
    const beneIndex = list.indexOf(event.beneficiary);
    if (beneIndex < 0) return;

    const selectedPerson = this.householdPeople.find(p => p.householdMemberId === event.memberId);
    if (!selectedPerson) return;

    this.dupDetection.remove(list[beneIndex]);
    list[beneIndex] = {
      ...list[beneIndex],
      householdMemberId: selectedPerson.householdMemberId,
      firstName: selectedPerson.name.firstName,
      lastName: selectedPerson.name.lastName,
      dateOfBirth: selectedPerson.birthDate || '',
      stateOfResidence: selectedPerson.state || '',
      isNew: false,
    };
    this.dupDetection.add(list[beneIndex]);
    this.emitState();
  }

  // --- Distribution ---

  handleDistributionChange(
    event: { beneficiary: BeneficiarySelection; value: number },
    section: BeneficiarySectionType
  ): void {
    const list = this.getBeneficiariesForSection(section);
    const beneIndex = list.indexOf(event.beneficiary);
    if (beneIndex < 0) return;

    list[beneIndex].distributionPercentage = event.value;
    this.updateDistributionErrors();
    this.emitState();
  }

  private updateDistributionErrors(): void {
    this.showPrimaryDistributionError = !this.isPrimaryValid;
    this.showBackupDistributionError = !this.isBackupValid;
  }

  // --- Section array accessors ---

  private getBeneficiariesForSection(section: BeneficiarySectionType): BeneficiarySelection[] {
    return section === 'PRIMARY'
      ? this.beneficiaryState.primaryBeneficiaries
      : this.beneficiaryState.backupBeneficiaries;
  }

  private setBeneficiariesForSection(section: BeneficiarySectionType, list: BeneficiarySelection[]): void {
    if (section === 'PRIMARY') {
      this.beneficiaryState.primaryBeneficiaries = list;
    } else {
      this.beneficiaryState.backupBeneficiaries = list;
    }
  }

  // --- Validation (called by parent AccountDetailsComponent) ---

  // Called by AccountDetailsComponent via @ViewChild on submit (line 433 of account-details.component.ts).
  // Returns true → parent blocks the confirm/next action.
  // Side effects are intentional: sets error flags consumed by this component's template.
  hasErrors(): boolean {
    this.showRequiredError = false;
    this.showPrimaryDistributionError = false;
    this.showBackupDistributionError = false;
    this.showIncompleteError = false;

    if (this.isInputCardOpen) {
      this.showIncompleteError = true;
      return true;
    }

    if (!this.hasBeneficiaries) {
      this.showRequiredError = true;
      return true;
    }

    this.updateDistributionErrors();
    return this.showPrimaryDistributionError || this.showBackupDistributionError;
  }

  // --- Emit state ---

  private emitState(): void {
    this.beneficiaryStateChange.emit({ ...this.beneficiaryState });
    // Emit validity for parent tracking without triggering inline error flags (those are set by hasErrors() on submit)
    this.validationChange.emit(this.hasBeneficiaries && this.isPrimaryValid && this.isBackupValid);
  }
}
